/**
 * @file admin-api.ts
 * @description Typed wrappers around the /admin/* endpoints. Only callable
 *   when the access token carries role=admin; the server enforces this and
 *   returns 403 otherwise.
 * @module Lib
 * @security The dev-only `dev_register_url` field is populated by the API
 *   ONLY when the worker runs in env=dev. In staging/prod it is absent.
 */

import { api } from './api';

export type UserRole = 'admin' | 'staff' | 'customer';

export interface CreateInvitationInput {
  email: string;
  role: UserRole;
  locale: 'he' | 'en';
  /** Optional display name (admin can also leave blank). */
  full_name?: string;
  /** Optional phone — 7..30 chars, digits/spaces/dashes/+/(). */
  phone?: string;
  /** Slice 86 — optional secondary contact email. CC'd on the invitation
   *  email + stored alongside the primary email on the customer row. */
  email_secondary?: string;
}

export interface CreateInvitationResponse {
  invitation_id: string;
  expires_at: string;
  email_delivered: boolean;
  dev_register_url?: string;
}

export interface InvitationRow {
  invitation_id: string;
  email: string;
  role: UserRole;
  expires_at: string;
  created_at: string;
}

export interface ListInvitationsResponse {
  invitations: InvitationRow[];
}

export function createInvitation(input: CreateInvitationInput): Promise<CreateInvitationResponse> {
  return api<CreateInvitationResponse>('/admin/invitations', {
    method: 'POST',
    json: input,
  });
}

export function listInvitations(): Promise<ListInvitationsResponse> {
  return api<ListInvitationsResponse>('/admin/invitations', { method: 'GET' });
}

export type CustomerStatus = 'active' | 'invited';
export type CustomerContactType = 'finance' | 'secretary' | 'other';

export interface CustomerContact {
  id: string;
  type: CustomerContactType;
  label: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export interface CustomerRow {
  id: string;
  email: string | null;
  status: CustomerStatus;
  full_name: string | null;
  project_count: number;
  /** Slice 66 — null = never signed in (typically invited rows). */
  last_login_at: string | null;
  /** Slice 75 — true when admin has notes on this customer. Only the
   *  boolean is on the list to keep note text out of bulk responses;
   *  the text lives on /admin/customers/:id (slice 74). */
  has_admin_notes: boolean;
  is_record_only?: boolean;
}

export interface ListCustomersResponse {
  customers: CustomerRow[];
}

export function listCustomers(): Promise<ListCustomersResponse> {
  return api<ListCustomersResponse>('/admin/customers', { method: 'GET' });
}

export interface CustomerDetail {
  id: string;
  email: string | null;
  status: CustomerStatus;
  full_name: string | null;
  phone: string | null;
  /** Slice 86 — optional secondary contact email (CC target). */
  email_secondary: string | null;
  /** Slice 65 — ISO-8601 of the last successful sign-in, or null if the
   *  customer has never logged in (e.g. still invited). */
  last_login_at: string | null;
  /** Slice 74 — admin-only freeform notes. Server returns this only
   *  from /admin/customers/:id (not /me). */
  admin_notes: string | null;
  contacts: CustomerContact[];
  is_record_only: boolean;
}

export interface CreateCustomerInput {
  full_name: string;
  locale: 'he' | 'en';
  contacts: CustomerContact[];
}

export function createCustomer(input: CreateCustomerInput): Promise<CustomerDetail> {
  return api<CustomerDetail>('/admin/customers', { method: 'POST', json: input });
}

export function getCustomer(id: string): Promise<CustomerDetail> {
  return api<CustomerDetail>(`/admin/customers/${encodeURIComponent(id)}`, { method: 'GET' });
}

export interface UpdateCustomerInput {
  /** `null` clears the name; empty string == null. */
  full_name?: string | null;
  /** `null` clears the phone; empty string == null. */
  phone?: string | null;
  /** Slice 86 — `null` / empty clears the secondary email. */
  email_secondary?: string | null;
  /** Slice 74 — `null` clears the notes; empty string trimmed → null. */
  admin_notes?: string | null;
  contacts?: CustomerContact[];
}

export function updateCustomer(id: string, input: UpdateCustomerInput): Promise<CustomerDetail> {
  return api<CustomerDetail>(`/admin/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    json: input,
  });
}

export interface ResendInvitationResponse {
  invitation_id: string;
  expires_at: string;
  email_delivered: boolean;
  dev_register_url?: string;
}

/** Slice 43: refire the invitation email for an 'invited'-status
 *  customer. Returns the new invitation's expiry + delivery status. */
export function resendInvitation(customerId: string): Promise<ResendInvitationResponse> {
  return api<ResendInvitationResponse>(
    `/admin/customers/${encodeURIComponent(customerId)}/resend-invitation`,
    { method: 'POST', json: {} },
  );
}

export interface AuditEvent {
  id: string;
  actor_id: string | null;
  actor_role: string | null;
  /**
   * Email of the actor at read time. Resolved server-side via LEFT JOIN
   * on users. May be null (system event, or hard-deleted user); for users
   * that went through DSAR delete this is the anonymized synthetic email.
   */
  actor_email: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  /** JSON string or null; UI parses on demand. May be missing-by-design. */
  before_state: string | null;
  after_state: string | null;
  ip_hash: string | null;
  user_agent: string | null;
  request_id: string;
  created_at: string;
}

export interface ListAuditFilters {
  actor_id?: string;
  action_prefix?: string;
  resource_type?: string;
  resource_id?: string;
  cursor?: string;
  limit?: number;
}

export interface ListAuditResponse {
  events: AuditEvent[];
  next_cursor: string | null;
}

export function listAudit(filters: ListAuditFilters = {}): Promise<ListAuditResponse> {
  const entries: Array<[string, string]> = [];
  const stringKeys: Array<keyof ListAuditFilters> = [
    'actor_id',
    'action_prefix',
    'resource_type',
    'resource_id',
    'cursor',
  ];
  for (const key of stringKeys) {
    const v = filters[key];
    if (typeof v === 'string' && v !== '') entries.push([key, v]);
  }
  if (filters.limit !== undefined) entries.push(['limit', String(filters.limit)]);
  const qs = new URLSearchParams(entries).toString();
  return api<ListAuditResponse>(`/admin/audit${qs === '' ? '' : `?${qs}`}`, { method: 'GET' });
}

export type AuditVerifyResponse =
  | { ok: true; total_rows: number; has_unchained_rows: boolean }
  | {
      ok: false;
      total_checked: number;
      broken_at_id: string;
      broken_at_created_at: string;
      reason: 'unchained' | 'prev_hash_mismatch' | 'row_hash_mismatch';
    };

export function verifyAuditChain(): Promise<AuditVerifyResponse> {
  return api<AuditVerifyResponse>('/admin/audit/verify', { method: 'GET' });
}

export interface AuditBackfillResponse {
  filled: number;
  scanned: number;
  already_complete: boolean;
}

export function backfillAuditChain(): Promise<AuditBackfillResponse> {
  return api<AuditBackfillResponse>('/admin/audit/backfill-chain', { method: 'POST' });
}

export interface ReadinessChecks {
  d1: 'ok' | 'fail' | 'timeout';
  kv_sessions: 'ok' | 'fail' | 'timeout';
  kv_rate_limit: 'ok' | 'fail' | 'timeout';
  r2: 'ok' | 'fail' | 'timeout';
}

export interface OpsStatusResponse {
  readiness: { status: 'ready' | 'degraded'; checks: ReadinessChecks; timestamp: string };
  audit_chain:
    | { status: 'ok'; total_rows: number; has_unchained_rows: boolean }
    | { status: 'broken'; broken_at_id: string; reason: string };
  backups: {
    latest_stamp: string | null;
    age_hours: number | null;
    status: 'ok' | 'warn' | 'fail';
  };
  dsar_exports: {
    pending: number;
    ready: number;
    failed: number;
    expired: number;
    overdue: number;
  };
  generated_at: string;
}

export function getOpsStatus(): Promise<OpsStatusResponse> {
  return api<OpsStatusResponse>('/admin/ops/status', { method: 'GET' });
}

export interface AdminDashboardSummary {
  active_customers: number;
  projects_awaiting_admin: number;
  files_in_review: number;
  actionable_quotes: number;
  /** Slice 60 — open milestones whose due_date < today. Surfaced as a
   *  red chip; mirrors the cron's predicate from slice 53. */
  overdue_milestones: number;
  /** Slice 71 — projects whose desired_deadline < today, still in
   *  flight. Same predicate as slice-69's per-row chip. */
  overdue_projects: number;
}

export function getAdminDashboard(): Promise<AdminDashboardSummary> {
  return api<AdminDashboardSummary>('/admin/dashboard', { method: 'GET' });
}
